import type { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import type { CurrencyCode } from '@percent/utility'
import type { SuppressibleEmailName } from './suppressibleEmailNames'

export type Program = {
  id: string
  name: string
  partnerId: string
  validationSubmissionConfigurationId?: string | null
  createdAt: string
  nominationsEnabled: boolean
  stateMachineDefinition: string
  opensAt?: string | null
  closesAt?: string | null
  budgetMin?: number
  budgetMax?: number
  currency?: CurrencyCode
  description?: string
  nominationFormId?: string
  nominationDeadline?: string | null
  nonprofitCcEmail?: string | null
  entryType: GrantProgramEntryType
  candidateRule?: GrantProgramCandidateRule
  shareNominatorInfoWithOrg?: boolean
  emailSuppressionConfig?: string[]
}

export type ProgramsResponse = PBPListResponse<Program>

export type ProgramResponse = PBPGetResponse<Program>

export type Form = {
  id: string
  title: string
  type?: string
  deadline?: string
  internalName: string
  partner?: { id: string; name: string } | null
  linkedTasksCount?: number
  createdAt?: string
  pages?: FormPage[]
}

export type GetFormsParams = {
  partnerId?: string
}

export type FormsResponse = PBPGetResponse<Form[]>

export type FormQuestionType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'name'
  | 'score'
  | 'award_amount'
  | 'final_award_amount'
  | 'documents'
  | 'radio'
  | 'checkbox'
  | 'decision'
  | 'select'
  | 'award_recommendation'
  | 'website'
  | 'country_search'
  | 'registry_search'
  | 'organisation_search'
  | 'date'

export type ComparisonOperator = '<' | '<=' | '>' | '>=' | '='

export type CandidateFieldComparisonValidationRule = {
  type: 'candidate_field_comparison'
  operator: ComparisonOperator
  candidateField: string
  percentage?: number
  errorMessage?: string
}

export type ValueComparisonValidationRule = {
  type: 'value_comparison'
  operator: ComparisonOperator
  value: number
  errorMessage?: string
}

export type ValidationRule = CandidateFieldComparisonValidationRule | ValueComparisonValidationRule

export type FormQuestionOption = {
  label: string
  value: string
  key?: string
  conditionalQuestionIds?: string[]
}

export type FormQuestion = {
  id: string
  key?: string
  label: string
  type: FormQuestionType
  order: number
  required: boolean
  multiple?: boolean
  hidden?: boolean
  maxScore?: number
  scoreWeight?: number
  options?: FormQuestionOption[]
  description?: string
  instructions?: string
  placeholder?: string
}

export type FormPage = {
  id: string
  title: string
  description: string
  order: number
  questions: FormQuestion[]
}

export type FormDetails = {
  id: string
  internalName?: string
  title?: string
  description?: string
  logo?: string
  primaryColor?: string
  secondaryColor?: string
  partner?: { id: string; name: string } | null
  pages: FormPage[]
}

export type FormDetailsResponse = PBPGetResponse<FormDetails>

export type TaskTemplate = {
  id: string
  name: string
  description?: string
  hoursUntilDue?: number
  dueByDate?: string
  expiresAt?: string | null
  possibleStatuses: string[]
  grantProgramId?: string
  details?:
    | { type: 'review' | 'decision'; formId: string }
    | { type: 'completion_event'; completionEventName: string }
    | { type: 'bank_details' }
  audience?: 'partner' | 'organisation'
}

export type TaskTemplatesResponse = PBPGetResponse<TaskTemplate[]>

export type CreateTaskTemplatePayload = Omit<TaskTemplate, 'id' | 'possibleStatuses' | 'grantProgramId'>

export type CreateTaskTemplateResponse = PBPGetResponse<TaskTemplate>

export type UpdateTaskTemplateResponse = PBPGetResponse<{ id: string }>

export type DeleteTaskTemplateParams = {
  grantProgramId: string
  taskTemplateId: string
}

export type AutoAssignmentPool = {
  id: string
  name: string
  partnerId: string
  createdAt?: Date
  accounts?: { accountId: string; email?: string }[]
  taskAutoAssignmentRules?: string[]
}

export type AutoAssignmentRule = {
  id: string
  name: string
  partnerId: string | null
  taskTemplates: { taskTemplateId: string }[]
  taskTemplateNames?: string[]
  countries: { countryCode: string }[]
  accounts: { accountId: string }[]
  nominatorGroups?: { nominatorGroup: string }[]
}
export type AutoAssignmentRulesResponse = PBPGetResponse<AutoAssignmentRule[]>

export type PostAssignmentRulePayload = {
  name: string
  taskTemplateIds: string[]
  countries: string[]
  accountIds: string[]
  nominatorGroups?: string[]
}

export type PatchAssignmentRulePayload = {
  name?: string
  taskTemplateIds?: string[]
  countries?: string[]
  accountIds?: string[]
  nominatorGroups?: string[]
}

export enum GrantProgramEntryType {
  APPLICATION = 'application',
  NOMINATION = 'nomination',
  INVITATION = 'invitation'
}

export enum GrantProgramCandidateRule {
  UNIQUE_PER_ORG = 'UNIQUE_PER_ORG',
  UNIQUE_PER_NOMINATION = 'UNIQUE_PER_NOMINATION'
}

export type UpdateGrantProgramPayload = {
  name?: string
  description?: string
  validationSubmissionConfigurationId?: string
  nominationFormId?: string
  opensAt?: string | null
  closesAt?: string | null
  budgetMin?: number
  budgetMax?: number
  currency?: string
  stateMachineDefinition?: string
  nominationDeadline?: string | null
  entryType?: GrantProgramEntryType
  candidateRule?: GrantProgramCandidateRule
  nonprofitCcEmail?: string | null
  shareNominatorInfoWithOrg?: boolean
  emailSuppressionConfig?: SuppressibleEmailName[]
}

export type AddGrantProgramPayload = {
  name: string
  description?: string
  partnerId: string
  validationSubmissionConfigurationId: string
  nominationFormId?: string
  opensAt?: string | null
  closesAt?: string | null
  budgetMin?: number
  budgetMax?: number
  currency?: CurrencyCode
  stateMachineDefinition?: string
  nominationDeadline?: string | null
  entryType: GrantProgramEntryType
  candidateRule: GrantProgramCandidateRule
  nonprofitCcEmail?: string | null
  shareNominatorInfoWithOrg: boolean
  emailSuppressionConfig?: SuppressibleEmailName[]
}

export type GetProgramsParams = {
  pageSize?: number
  cursor?: string
  search?: string
  sort?: string
  partnerId?: string
}

export type GetProgramParams = {
  programId: string
}

export type DonationStatus = 'processing' | 'paid'

export type ProgramCandidate = {
  id: string
  currentState: string
  organisation: {
    id: string | null
    name: string | null
    countryCode: string | null
  }
  rejectionReason?: string
  createdAt: Date
  deletedAt: Date | null
  award?: {
    currency: CurrencyCode
    amount: number
  }
  paymentDetails?: {
    disbursedBy: string
    payment?: {
      amount: number
      currency: CurrencyCode
    }
    status: DonationStatus
    paidAt?: Date
  }
}

export type ProgramCandidatesResponse = PBPListResponse<ProgramCandidate>

export type GetProgramCandidatesParams = {
  programId: string
  pageSize?: number
  direction?: 'ASC' | 'DESC'
  sortBy?: 'submittedAt' | 'paidAt'
  search?: string
  currentState?: string[]
}

export type UpdateProgramCandidateParams = {
  orgId?: string
  orgContactEmail?: string
  orgContactFirstName?: string
  orgContactAccountId?: string
}

export type AdminProgramCandidate = {
  orgContactEmail: string
  orgContactFirstName: string
  orgContactLastName: string
  orgContactAccountId: string
} & ProgramCandidate

export type AdminCandidateResponse = PBPGetResponse<AdminProgramCandidate>

export type TaskStatus = 'todo' | 'done' | 'cancelled'

export type Task = {
  id: string
  status: TaskStatus
  createdAt: string
  completedAt?: string
  dueByDate?: string
  name: string
  type: 'review' | 'bank_details'
  formId?: string
  audience: 'partner' | 'organisation'
  assignee?: {
    email: string
    accountId: string
  }
}

export type CandidateTasksResponse = PBPGetResponse<Task[]>

export type UpdateTaskStatusPayload = {
  status: TaskStatus
}

export type TaskAssignee = {
  id: string
  email: string
}

export type TaskAssigneesResponse = PBPListResponse<TaskAssignee>

export type UpdateTaskAssigneePayload = {
  taskId: string
  assignedTo: string
}

export type StateMachineMetadataOption = {
  value: string
  label: string
}

export type StateMachineMetadataV2Block = {
  conditionTypes: ('set' | 'numeric')[]
  matchModes: ('any' | 'all')[]
  organisationConditionFields: ('country' | 'activityTags' | 'orgType' | 'id')[]
}

export type StateMachineMetadata = {
  eventNames: StateMachineMetadataOption[]
  emailTemplates: string[]
  slackTemplates: string[]
  taskTemplates: { id: string; name: string; details?: TaskTemplate['details'] }[]
  displayModes: string[]
  tableTypes: string[]
  comparisonOperators: string[]
  logicalOperators: string[]
  eventPayloadFields: Record<string, { field: string; type: string }[]>
  v2?: StateMachineMetadataV2Block
}

export type StateMachineMetadataResponse = PBPGetResponse<StateMachineMetadata>

export type FormCopy = Partial<
  Record<
    | 'en-US'
    | 'en-GB'
    | 'nl-NL'
    | 'fr-FR'
    | 'fr-BE'
    | 'de-DE'
    | 'de-CH'
    | 'de-AT'
    | 'it-IT'
    | 'ja-JP'
    | 'ko-KR'
    | 'pt-BR'
    | 'pt-PT'
    | 'es-MX'
    | 'es-ES'
    | 'es-419'
    | 'fi-FI'
    | 'el-GR'
    | 'pl-PL'
    | 'sv-SE'
    | 'ro-RO',
    string
  >
>

export type CreateStandaloneFormQuestionPayload = {
  key: string
  label: FormCopy
  type: FormQuestionType
  placeholder?: FormCopy
  description?: FormCopy
  instructions?: FormCopy
  dashboardLabel?: string
  required?: boolean
  autoTranslationsEnabled?: boolean
  hidden?: boolean
  partnerId?: string
  multiple?: boolean
  options?: { key: string; label: FormCopy; conditionalQuestionIds?: string[] }[]
  maxScore?: number
  scoreWeight?: number
  maxLength?: number
  addOrganisationQuestions?: string[]
  contactType?: 'LEGAL_SIGNATORY' | 'COMMS_PR_LEAD' | 'FINANCE_LEAD' | 'EXECUTIVE_LEAD'
  nameType?: 'first' | 'last'
  validationRules?: ValidationRule[]
}

export type CreateStandaloneFormQuestionResponse = PBPGetResponse<{ questionId: string }>

export type UpdateStandaloneFormQuestionPayload = {
  type: FormQuestionType
  key?: string
  label?: FormCopy
  placeholder?: FormCopy
  description?: FormCopy
  instructions?: FormCopy
  dashboardLabel?: string | null
  required?: boolean
  autoTranslationsEnabled?: boolean
  hidden?: boolean
  multiple?: boolean
  options?: { key: string; label: FormCopy; conditionalQuestionIds?: string[] }[]
  maxScore?: number
  scoreWeight?: number
  maxLength?: number
  contactType?: string
  nameType?: string
  addOrganisationQuestions?: string[]
  validationRules?: ValidationRule[]
}

export type LocalisedCopy = {
  id: string
  locales: {
    formCopyId: string
    locale: string
    value: string
  }[]
}

export type StandaloneFormQuestion = {
  id: string
  key: string
  type: FormQuestionType
  labelId: string | null
  dashboardLabel: string | null
  partnerId: string | null
  required: boolean
  createdAt: string
  localisedLabel: LocalisedCopy | null
}

export type StandaloneFormQuestionDetail = {
  id: string
  key: string
  type: FormQuestionType
  label: string
  placeholder: string
  description: string
  instructions?: string
  dashboardLabel: string | null
  required: boolean
  autoTranslationsEnabled?: boolean
  hidden: boolean
  order: number
  multiple?: boolean
  options?: {
    /** Option row id. NOT the option key — do not send this back in a PATCH payload. */
    value: string
    /** Stable option key the API expects on write. Only present once the API exposes it. */
    key?: string
    /** Option row id, sent back on update so the API can match an existing row instead of recreating it. */
    id?: string
    label: string
    conditionalQuestionIds?: string[]
  }[]
  maxScore?: number
  scoreWeight?: number
  maxLength?: number
  contactType?: string
  nameType?: string
  addOrganisationQuestions?: string[]
  validationRules?: ValidationRule[]
}

export type StandaloneFormQuestionDetailResponse = PBPGetResponse<StandaloneFormQuestionDetail>

export type GetStandaloneFormQuestionsParams = {
  pageSize?: number
  cursor?: string
  partnerId?: string
  search?: string
  questionIds?: string[]
}

export type StandaloneFormQuestionsResponse = PBPListResponse<StandaloneFormQuestion>

export type CreateFormQuestionRef = {
  id: string
}

export type CreateFormPage = {
  title: FormCopy
  description?: FormCopy
  questions?: CreateFormQuestionRef[]
}

export type CreateFormPayload = {
  internalName: string
  pages: CreateFormPage[]
  logo?: string
  title?: FormCopy
  description?: FormCopy
  postSubmitTitle?: FormCopy
  postSubmitDescription?: FormCopy
  primaryColor?: string
  secondaryColor?: string
  partnerId?: string
}

export type CreateFormResponse = PBPGetResponse<{ id: string }>

export type UpdateFormPayload = {
  internalName?: string
  pages?: CreateFormPage[]
  logo?: string
  title?: FormCopy
  description?: FormCopy
  postSubmitTitle?: FormCopy
  postSubmitDescription?: FormCopy
  primaryColor?: string
  secondaryColor?: string
}

export type GetFormsListParams = {
  pageSize?: number
  cursor?: string
  partnerId?: string
  search?: string
  internalName?: string
  formIds?: string[]
}

export type FormsListResponse = PBPListResponse<Form>
